<script setup>
import { l } from '@utils/toolbox';
import { useCalendar } from '../composables/useCalendar';

const props = defineProps({
	day: { type: Object, required: true }
});

const { getLabel, handleDayClick, getPriceForDate } = useCalendar();
</script>

<template>
  <div :class="day.cellClasses">
    <div :class="day.weekClass">
      <span>{{ day.weekNo }}</span>
    </div>
    <div
      class="Calendar__day"
      :class="day.dayClasses"
      :data-date="day.dateISO"
      :aria-label="day.dateFormattedLong"
      :tabindex="day.isDisabled ? -1 : 0"
      role="gridcell"
      @click="handleDayClick(day.dateISO)"
    >
      <span class="Calendar__date">{{ day.day }}</span>

      <span v-if="day.isDisabled" />
      <span v-else-if="day.queued" class="Calendar__price Calendar__price--queued">
        {{ l('priceCalendar.information.queuedText') }}
      </span>
      <span v-else-if="day.error" class="Calendar__price Calendar__price--error">
        {{ l('priceCalendar.information.noFlights') }}
      </span>

      <span
        v-else-if="day.loading"
        class="Calendar__price Calendar__price--loading"
        :style="{ '--progress': `${day.progress}%` }"
        :aria-label="`${day.progress}%`"
      />

      <span
        v-else
        class="Calendar__price"
        :class="{
          'Calendar__price--resolved': getPriceForDate(day.dateISO),
          'Calendar__price--cheapest': day.isCheapest
        }"
      >
        {{ getLabel(day.dateISO) }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.Calendar__day--holiday::after {
  top: 1rem;
  right: 1rem;

	@include breakpoint_down(md) {
    right: 1rem;
  }
}

.Calendar__day {
  height: 6rem;
  margin-top: .35rem;
  gap: .25rem;
  transition: background-color .6s ease;
  border-radius: $borderRadius-sm;

  &:hover {
    background-color: $color-green-100;
  }
}

.Calendar__day--active {
  border: 0.1rem solid $color-blue-400;
  box-sizing: content-box;
  border-radius: .9rem;
}

.Calendar__price {
  font-size: 1.4rem;
  line-height: 1;
  color: $color-grey-700;
  text-align: center;

	@include breakpoint_down(md) {
    font-size: 1.2rem;
    letter-spacing: -.7px;
  }
}

.Calendar__price--max-short .Calendar__price {
	@include breakpoint_down(md) {
    font-size: 1.4rem;
  }
}

.Calendar__price--max-long .Calendar__price {
	@include breakpoint_down(md) {
    font-size: 1.1rem;
  }
}

.Calendar__price--resolved {
  font-weight: 800;
  color: $black;
}

.Calendar__price--queued {
  color: $color-grey-600;
}

.Calendar__price--loading {
  font-size: 0;
  width: calc(100% - 4rem);
  height: 1.2rem;
  border-radius: 999px;
  overflow: hidden;
  background-color: $color-grey-200;
  min-width: 3rem;

  &::after {
    content: '';
    display: block;
    width: var(--progress, 0%);
    height: 100%;
    background-color: $color-blue-500;
    transition: width 0.1s ease-out;
    transform: skew(9deg, 0);
  }
}

.Calendar__price.Calendar__price--cheapest {
  &::after {
    content: "";
    width: 4rem;
    height: 0.5rem;
    background-color: $color-green-500;
    color: $color-green-500;
    border-radius: $borderRadius-md;
    position: absolute;
    left: calc(50% - 2rem);
    bottom: 0.25rem;
  }
}

.Calendar__day--active {
  .Calendar__date {
    border: none;
    background-color: transparent;
    font-weight: 400;
    width: auto;
    height: auto;
  }
}

.Calendar__cell {
  height: 7rem;
}

.Calendar__month {
  text-align: center;
  text-transform: capitalize;
}
</style>
